import React, { createContext, useContext, useState, useEffect, lazy } from 'react';
import { useLocation } from 'react-router-dom';

// Create a placeholder context for non-admin routes
const PlaceholderAdminAuthContext = createContext({
  currentAdmin: null,
  isAdminAuthenticated: false,
  loading: false,
  error: null,
  loginAdmin: async () => {},
  logoutAdmin: async () => {}
});

// Lazy load the actual AdminAuthProvider
let AdminAuthProviderComponent = null;
const loadAdminAuthProvider = async () => {
  if (!AdminAuthProviderComponent) {
    const module = await import('./AdminAuthContext');
    AdminAuthProviderComponent = module.AdminAuthProvider;
  }
  return AdminAuthProviderComponent;
};

export const useAdminAuth = () => useContext(PlaceholderAdminAuthContext);

/**
 * Conditional AdminAuthProvider that only loads on admin routes
 * This optimizes initial page load for non-admin users
 */
export const ConditionalAdminAuthProvider = ({ children }) => {
  const location = useLocation();
  const [AdminProvider, setAdminProvider] = useState(null);
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdminRoute && !AdminProvider) {
      loadAdminAuthProvider().then(Provider => {
        setAdminProvider(() => Provider);
      });
    }
  }, [isAdminRoute, AdminProvider]);

  // If on admin route and provider is loaded, use it
  if (isAdminRoute && AdminProvider) {
    return <AdminProvider>{children}</AdminProvider>;
  }

  // Otherwise use placeholder context
  return (
    <PlaceholderAdminAuthContext.Provider
      value={{
        currentAdmin: null,
        isAdminAuthenticated: false,
        loading: false,
        error: null,
        loginAdmin: async () => {
          throw new Error('Admin authentication not loaded');
        },
        logoutAdmin: async () => {}
      }}
    >
      {children}
    </PlaceholderAdminAuthContext.Provider>
  );
};

export default ConditionalAdminAuthProvider;
